<script>
  export default {
    name: 'UnitModal',
    emits: ['cancel'],
    data() {
      return {
        flipped: false,
        cardVariant: 1,
        switching: false,
        flipTransitionActive: false,
        deckLeavingSrc: null,
        deckRevealSrc: null,
        pendingVariant: null,
        deckRevealedOnTop: false,
        deckSwitchPromoteTimer: null,
      }
    },
    props: {
      imgSrc: {
        type: String,
        default: ''
      },
      backImgSrc: {
        type: String,
        default: ''
      },
      imgSrc2: {
        type: String,
        default: ''
      },
      backImgSrc2: {
        type: String,
        default: ''
      },
    },
    computed: {
      hasAlternate() {
        return Boolean(this.imgSrc2);
      },
      imageKey() {
        return `${this.cardVariant}-${this.flipped ? 'back' : 'front'}`;
      },
      underPeekSrc() {
        if (!this.hasAlternate || this.flipTransitionActive) return '';
        if (this.deckLeavingSrc || this.deckRevealSrc) return '';
        if (this.flipped) {
          return this.cardVariant === 1
            ? (this.backImgSrc2 || '')
            : this.backImgSrc;
        }
        return this.cardVariant === 1 ? this.imgSrc2 : this.imgSrc;
      },
      activeBackSrc() {
        if (this.cardVariant === 2) {
          return this.backImgSrc2 || this.backImgSrc;
        }
        return this.backImgSrc;
      },
      showMainCard() {
        return !this.deckLeavingSrc;
      },
    },
    watch: {
      imgSrc() {
        this.resetCardState();
      },
    },
    methods: {
      resetCardState() {
        this.flipped = false;
        this.cardVariant = 1;
        this.switching = false;
        this.flipTransitionActive = false;
        this.deckLeavingSrc = null;
        this.deckRevealSrc = null;
        this.pendingVariant = null;
        this.deckRevealedOnTop = false;
        this.clearDeckSwitchTimer();
      },
      clearDeckSwitchTimer() {
        if (this.deckSwitchPromoteTimer) {
          window.clearTimeout(this.deckSwitchPromoteTimer);
          this.deckSwitchPromoteTimer = null;
        }
      },
      imageSrcByKey(key) {
        const [variantPart, face] = key.split('-');
        const variant = variantPart === '2' ? 2 : 1;
        if (face === 'back') {
          if (variant === 2) {
            return this.backImgSrc2 || this.backImgSrc;
          }
          return this.backImgSrc;
        }
        return variant === 2 ? this.imgSrc2 : this.imgSrc;
      },
      onFlipBeforeLeave() {
        this.flipTransitionActive = true;
      },
      onFlipBeforeEnter() {
        this.flipTransitionActive = true;
      },
      onFlipAfterEnter() {
        this.flipTransitionActive = false;
      },
      flip() {
        if (!this.activeBackSrc || this.deckLeavingSrc) return;
        this.flipTransitionActive = true;
        this.flipped = !this.flipped;
      },
      switchVariant() {
        if (!this.hasAlternate || this.switching || this.deckLeavingSrc) return;

        const nextVariant = this.cardVariant === 1 ? 2 : 1;
        const face = this.flipped ? 'back' : 'front';
        this.pendingVariant = nextVariant;
        this.deckLeavingSrc = this.imageSrcByKey(`${this.cardVariant}-${face}`);
        this.deckRevealSrc = this.imageSrcByKey(`${nextVariant}-${face}`);
        this.deckRevealedOnTop = false;
        this.switching = true;
        this.clearDeckSwitchTimer();
        // Sync with ~48% of deck-leaving-wrap — card at arc, return path tucks under revealed
        this.deckSwitchPromoteTimer = window.setTimeout(() => {
          this.deckRevealedOnTop = true;
        }, 340);
      },
      onDeckLeaveEnd(event) {
        if (event.target !== event.currentTarget) return;
        if (!event.animationName.includes('deck-leaving-wrap')) return;
        if (!this.deckLeavingSrc) return;

        this.clearDeckSwitchTimer();
        if (this.pendingVariant) {
          this.cardVariant = this.pendingVariant;
        }
        this.pendingVariant = null;
        this.deckLeavingSrc = null;
        this.deckRevealSrc = null;
        this.deckRevealedOnTop = false;
        this.switching = false;
      },
    },
  }
</script>

<template>
  <vue-final-modal
   v-slot="{ params, close }"
   v-bind="$attrs"
   classes="modal-container"
   content-class="modal-content">

   <div v-if="hasAlternate" class="modal__buttons-left">
     <button
       type="button"
       class="modal__switch button button_round button_icon"
       :disabled="switching"
       @click="switchVariant"
     >
       <img
         v-if="cardVariant === 1"
         src="@/images/img/card_switch_2.png"
         alt=""
       >
       <img
         v-else
         src="@/images/img/card_switch_1.png"
         alt=""
       >
     </button>
   </div>

   <div class="modal__buttons">
     <button @click="$emit('cancel', close)" class="modal__close button button_round button_icon">
       <img src="@/images/img/close.png" alt="">
     </button>
     <button
       type="button"
       class="modal__flip button button_round button_icon"
       v-if="activeBackSrc"
       @click="flip"
     >
       <img src="@/images/img/flip.png" alt="">
     </button>
   </div>

   <div class="modal__image-container" :class="{ 'modal__image-container--deck-switch': deckLeavingSrc }">
     <img
       v-if="deckRevealSrc"
       v-image-fallback
       :src="deckRevealSrc"
       class="modal__image modal__image--revealed modal__image--deck-promote"
       :class="{ 'modal__image--revealed-top': deckRevealedOnTop }"
       alt=""
       aria-hidden="true"
     >

     <img
       v-else-if="underPeekSrc"
       v-image-fallback
       :src="underPeekSrc"
       class="modal__card-under"
       alt=""
       aria-hidden="true"
     >

     <Transition
       v-if="showMainCard"
       name="flip"
       mode="out-in"
       @before-leave="onFlipBeforeLeave"
       @before-enter="onFlipBeforeEnter"
       @after-enter="onFlipAfterEnter"
     >
       <img
         :key="imageKey"
         v-image-fallback
         :src="imageSrcByKey(imageKey)"
         class="modal__image"
         alt=""
       />
     </Transition>

     <div
       v-if="deckLeavingSrc"
       class="modal__deck-leaving-wrap"
       :class="{ 'modal__deck-leaving-wrap--under': deckRevealedOnTop }"
       @animationend="onDeckLeaveEnd"
     >
       <img
         v-image-fallback
         :src="deckLeavingSrc"
         class="modal__deck-leaving"
         alt=""
       >
     </div>
   </div>

  </vue-final-modal>
</template>

<style scoped>
::v-deep(.modal-container) {
  display: flex;
  justify-content: center;
  align-items: center;
}
::v-deep(.modal-content) {
  position: relative;
  display: flex;
  flex-direction: column;
  max-height: 90%;
  margin: 0 1rem;
  padding: 1rem;
  max-width: 420px;
  border-radius: 0.25rem;
  background: transparent;
}

.modal__buttons-left {
  position: absolute;
  top: 0.5rem;
  left: 0.5rem;
  z-index: 10;
}

.modal__buttons {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  z-index: 10;
}

.modal__close,
.modal__flip,
.modal__switch {
  transition: all 0.2s ease;
}

.modal__close:hover,
.modal__flip:hover,
.modal__switch:hover:not(:disabled) {
  transform: scale(1.1);
  filter: brightness(1.2);
  cursor: pointer;
}

.modal__close:active,
.modal__flip:active,
.modal__switch:active:not(:disabled) {
  transform: scale(0.95);
}

.modal__switch:disabled {
  opacity: 0.6;
  cursor: default;
}

.modal__image-container {
  position: relative;
  width: 100%;
  height: 500px;
  display: flex;
  justify-content: center;
  align-items: center;
  perspective: 1000px;
}

.modal__card-under {
  position: absolute;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  transform: translate(4px, 6px) scale(0.98);
  opacity: 0.85;
  z-index: 0;
  pointer-events: none;
}

.modal__image {
  position: relative;
  z-index: 1;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.modal__image--revealed {
  position: absolute;
  left: 50%;
  top: 50%;
  z-index: 0;
  opacity: 1;
  transform: translate(-50%, -50%);
}

.modal__image--revealed-top {
  z-index: 2;
}

.modal__image--deck-promote {
  animation: deck-promote 0.7s cubic-bezier(0.45, 0.05, 0.25, 1) forwards;
}

.modal__deck-leaving-wrap {
  position: absolute;
  inset: 0;
  margin: auto;
  z-index: 3;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  pointer-events: none;
  animation: deck-leaving-wrap 0.7s cubic-bezier(0.45, 0.05, 0.25, 1) forwards;
  transform-origin: 50% 88%;
}

.modal__deck-leaving-wrap--under {
  z-index: 0;
}

.modal__deck-leaving {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

@keyframes deck-leaving-wrap {
  0% {
    transform: translate(0, 0) scale(1) rotate(0deg);
    filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.2));
    opacity: 1;
  }
  12% {
    transform: translate(0, -14px) scale(1.02) rotate(0deg);
    filter: drop-shadow(0 20px 30px rgba(0, 0, 0, 0.36));
  }
  38% {
    transform: translate(-26%, -30%) scale(1.02) rotate(-10deg);
    filter: drop-shadow(0 24px 34px rgba(0, 0, 0, 0.3));
  }
  48% {
    transform: translate(-26%, -30%) scale(1.02) rotate(-10deg);
    filter: drop-shadow(0 24px 34px rgba(0, 0, 0, 0.3));
  }
  58% {
    transform: translate(-18%, -20%) scale(1.01) rotate(-7deg);
    filter: drop-shadow(0 18px 26px rgba(0, 0, 0, 0.26));
  }
  68% {
    transform: translate(-8%, -8%) scale(1) rotate(-3deg);
    filter: drop-shadow(0 12px 20px rgba(0, 0, 0, 0.2));
  }
  82% {
    transform: translate(2px, 3px) scale(0.99) rotate(-2deg);
    filter: drop-shadow(0 4px 10px rgba(0, 0, 0, 0.14));
  }
  100% {
    transform: translate(4px, 6px) scale(0.98) rotate(-2deg);
    filter: drop-shadow(0 1px 4px rgba(0, 0, 0, 0.1));
    opacity: 0.88;
  }
}

@keyframes deck-promote {
  0%,
  50% {
    transform: translate(-50%, -50%) scale(0.992);
  }
  58% {
    transform: translate(-50%, -50%) scale(0.998);
  }
  68%,
  100% {
    transform: translate(-50%, -50%) scale(1);
  }
}

.modal__title {
  margin: 0 2rem 0 0;
  font-size: 1.5rem;
  font-weight: 700;
}

.modal__action {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  padding: 1rem 0 0;
}

@media (max-width: 767px) {
  ::v-deep(.modal-content) {
    max-width: 70%;
  }

  .modal__buttons-left {
    top: 0.25rem;
    left: 0.25rem;
  }

  .modal__buttons {
    top: 0.25rem;
    right: 0.25rem;
    gap: 0.25rem;
  }

  .modal__image-container {
    height: 420px;
  }
}

@media (min-width: 768px) and (max-width: 1024px) {
  .modal__image-container {
    height: 600px;
  }
}

@media (min-width: 1025px) {
  .modal__image-container {
    height: 700px;
  }
}
</style>

<style scoped>
.dark-mode div ::v-deep(.modal-content) {
  border-color: #2d3748;
  background-color: #1a202c;
}
</style>

<style scoped>
  .flip-enter-active, .flip-leave-active {
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    transform-style: preserve-3d;
  }

  .flip-leave-active {
    position: absolute;
    z-index: 1;
  }

  .flip-enter-from,
  .flip-leave-to {
    transform: rotateY(90deg);
    opacity: 0;
  }

  .flip-enter-to,
  .flip-leave-from {
    transform: rotateY(0deg);
    opacity: 1;
  }
</style>
